import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WarningModal from 'app/shared-components/ModalActionsCard/WarningModalNew';
import { sentryMaskIf } from 'sentryReplayPrivacy';

export interface RemovePasskeyDialogProps {
  isOpen: boolean;
  /** Label of the passkey being removed, shown so the user can confirm which. */
  passkeyName: string;
  /** Removing the account's only passkey — adds the fallback-to-password warning. */
  isLastPasskey: boolean;
  isRemoving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * SEE-226 removal confirmation, shared by the web Security tab and the mobile
 * Security page. ResponsiveDialog underneath goes full-screen on mobile, so one
 * component covers both surfaces.
 */
export default function RemovePasskeyDialog({
  isOpen,
  passkeyName,
  isLastPasskey,
  isRemoving,
  onClose,
  onConfirm,
}: RemovePasskeyDialogProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <WarningModal
      isOpen={isOpen}
      isLoading={isRemoving}
      onClose={onClose}
      onContinue={onConfirm}
      modalVariant="error"
      // Figma 2840:49940 gives the last-passkey case its own title and body —
      // it's a different decision, not the same one with a warning appended.
      modalHeader={isLastPasskey ? t('passkeyRemoveLastTitle') : t('passkeyRemoveTitle')}
      continueButtonText={t('passkeyRemoveConfirm')}
    >
      <Typography
        // The body names the passkey; the last-passkey body doesn't.
        {...sentryMaskIf(!isLastPasskey)}
        sx={{ fontSize: '14px', lineHeight: '20px', letterSpacing: '0.25px' }}
      >
        {isLastPasskey ? t('passkeyRemoveLastBody') : t('passkeyRemoveBody', { name: passkeyName })}
      </Typography>
    </WarningModal>
  );
}
